import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { SKILL_LABELS, SKILL_ORDER, clbTone } from "@/lib/tcf";
import { TrendingUp, Target, BookOpen, Headphones, PenLine, Mic, ClipboardList } from "lucide-react";

const SKILL_ICONS = { reading: BookOpen, listening: Headphones, writing: PenLine, speaking: Mic };
const SKILL_CTA = {
  reading: { to: "/tests", label: "Take a test" },
  listening: { to: "/tests", label: "Take a test" },
  writing: { to: "/writing-lab", label: "Try Writing Lab" },
  speaking: { to: "/coaches/speaking_coach", label: "Practice speaking" },
};

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString("en-CA", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export default function ScoreTracking() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/tcf/score-history").then((r) => setData(r.data)).catch((e) => console.error("history", e)); }, []);

  const latest = data?.latest_by_skill || {};
  const target = data?.target_clb ?? 7;
  const clbs = SKILL_ORDER.map((s) => latest[s]?.clb).filter((c) => c != null);
  const overall = clbs.length ? Math.min(...clbs) : null;
  const empty = !data || !data.history || data.history.length === 0;

  return (
    <div data-testid="score-page">
      <h1 className="font-display text-4xl font-semibold">Score Tracking</h1>
      <p className="text-muted-foreground mt-2">Your latest estimated CLB per skill vs your target, plus full history.</p>

      {empty ? (
        <div className="mt-12 rounded-2xl bg-card border border-border p-12 text-center">
          <TrendingUp className="h-10 w-10 mx-auto text-muted-foreground" />
          <p className="mt-4 font-semibold text-lg">No results yet</p>
          <p className="text-muted-foreground">Take the diagnostic or a mock test to start tracking your CLB.</p>
          <Link to="/tests" className="mt-5 inline-block px-6 py-3 rounded-full bg-primary text-primary-foreground font-semibold">Take a test</Link>
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
            <div className="rounded-2xl emerald-surface grain relative overflow-hidden p-6 text-white">
              <span className="text-white/80 text-sm inline-flex items-center gap-2"><Target className="h-4 w-4" /> Overall (min)</span>
              <div className="mt-1 font-display text-5xl font-semibold">CLB {overall ?? "—"}</div>
              <p className="text-white/80 text-sm mt-1">Target: CLB {target}</p>
            </div>
            {SKILL_ORDER.map((s) => {
              const v = latest[s];
              const Icon = SKILL_ICONS[s];
              return (
                <div key={s} className="rounded-2xl bg-card border border-border p-6" data-testid={`score-skill-${s}`}>
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-2 text-sm font-medium"><Icon className="h-4 w-4 text-primary" /> {SKILL_LABELS[s]}</span>
                    {v && <span className="text-xs text-muted-foreground">{v.cefr}</span>}
                  </div>
                  <div className={`mt-2 font-display text-4xl font-semibold ${clbTone(v?.clb, target)}`}>{v ? `CLB ${v.clb}` : "—"}</div>
                  {v ? (
                    <div className="mt-3 h-1.5 w-full rounded-full bg-secondary overflow-hidden">
                      <div className="h-full bg-primary" style={{ width: `${Math.min(100, (v.clb / 10) * 100)}%` }} />
                    </div>
                  ) : (
                    <Link to={SKILL_CTA[s].to} data-testid={`score-cta-${s}`} className="mt-2 inline-block text-xs font-semibold text-primary hover:underline">{SKILL_CTA[s].label} →</Link>
                  )}
                </div>
              );
            })}
          </div>

          <h2 className="mt-10 font-display text-2xl font-semibold flex items-center gap-2"><ClipboardList className="h-5 w-5 text-primary" /> History</h2>
          <div className="mt-4 space-y-2">
            {data.history.map((h, i) => (
              <motion.div key={h.id || i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.03 }}
                className="rounded-xl bg-card border border-border px-5 py-4 flex items-center justify-between gap-4" data-testid={`history-${i}`}>
                <div className="min-w-0">
                  <p className="font-medium truncate">{h.title}</p>
                  <p className="text-xs text-muted-foreground">{fmtDate(h.created_at)} · {h.kind}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap justify-end">
                  {Object.entries(h.skills || {}).map(([s, v]) => (
                    <span key={s} className="text-xs px-2 py-1 rounded-full bg-secondary font-semibold" title={SKILL_LABELS[s]}>
                      {s[0].toUpperCase()}: CLB {v.clb}
                    </span>
                  ))}
                  {h.overall_clb != null && <span className="text-sm font-display font-semibold text-primary">CLB {h.overall_clb}</span>}
                </div>
              </motion.div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
